import React, { createContext, useState, useEffect, useRef } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { clearUsersCache, findUserById, findUserByIdResilient, recordTrainingChecked } from "../lib/data";
import { auth, db } from "../lib/firebase";
import {
  onAuthStateChanged,
  reload,
  signInWithEmailAndPassword,
  signOut,
} from "@firebase/auth";
import type { User } from "../types";
import { getDevicePushTokens, setupPush } from "../setupPush";
import { withTimeout } from "../lib/async";
import { arrayRemove, doc, getDocFromServer, onSnapshot, updateDoc } from "@firebase/firestore";
import { triggerOnboardingFlowAction } from "../lib/actions";
import { functions } from "../lib/firebase";
import { httpsCallable } from "@firebase/functions";
import { updateUserProfile } from "../lib/data";
import { clearLocalSnapshots, loadProfileSnapshot, loadSignedInUidHint, saveProfileSnapshot } from "../lib/local-snapshot";
import { diag } from "../lib/diag";
import { loginWasEnded } from "../lib/sessions";
import { hasActiveSubscription } from "../lib/subscription";

export type GuidedTourStep =
  | "start"
  | "chat_icon"
  | "ursa_chat"
  | "postavi_profil_button"
  | "profile_icon"
  | "uredi_profil_button"
  | "promijeni_sliku_button"
  | "spremi_promjene_avatar_button"
  | "progresija_tab"
  | "dodaj_napredak_card"
  | "spremi_napredak_button"
  | "done";

interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  loading: boolean;
  login: (email: string, pass: string) => Promise<LoginResult>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  unreadChatCount: number;
  setUnreadChatCount: React.Dispatch<React.SetStateAction<number>>;
  unreadCountsMap: Record<string, number>;
  setUnreadCountsMap: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  fcmToken: string | null;
  setFcmToken: React.Dispatch<React.SetStateAction<string | null>>;
  tourStep: GuidedTourStep | null;
  setTourStep: React.Dispatch<React.SetStateAction<GuidedTourStep | null>>;
  toggleMuteGroup: (groupId: string) => Promise<void>;
  /** True while a user who finished onboarding re-runs the profile tour. */
  tourReplay: boolean;
  startTourReplay: () => void;
  endTourReplay: () => void;
}

export type LoginResult =
  | { ok: true }
  | { ok: false; reason: "invalid-credentials" | "no-account" | "network" | "unknown" };

export const AuthContext = createContext<AuthContextType | null>(null);

/**
 * The server's copy of the profile, or the current object when nothing
 * changed: a new object makes every screen that depends on the user reload
 * (and e.g. dropped a meal-plan edit made in the first seconds).
 */
function replaceIfChanged(prev: User | null, fresh: User): User | null {
  if (prev?.id !== fresh.id) return prev;
  try {
    if (JSON.stringify(prev) === JSON.stringify(fresh)) return prev;
  } catch {
    // Not comparable: take the fresh copy.
  }
  return { ...fresh };
}

/**
 * Whether the account behind this login was really deleted. A profile read
 * at app start sometimes came back "not found" for an account that exists,
 * and the app signed the person out for it (twice a day on an iPhone).
 * Deleting a profile also deletes its login (cleanupAuthOnUserDelete), so
 * the login is asked first; the profile is then read from the server again
 * after each pause in `rechecks` (ms), and must be missing every time.
 * Anything inconclusive (no connection) keeps the person in.
 */
async function accountIsGone(uid: string, rechecks = [2000, 4000]): Promise<boolean> {
  const current = auth.currentUser;
  if (!current || current.uid !== uid) return false;
  try {
    await withTimeout(reload(current), 10000, "check login");
  } catch (error) {
    const code = (error as { code?: string })?.code ?? "";
    const gone = ["auth/user-not-found", "auth/user-disabled", "auth/user-token-expired", "auth/invalid-user-token"].includes(code);
    diag("login check", { gone, code: code || String(error).slice(0, 60) });
    return gone;
  }
  for (const [index, pause] of rechecks.entries()) {
    const attempt = index + 1;
    await new Promise((resolve) => setTimeout(resolve, pause));
    try {
      const snap = await withTimeout(getDocFromServer(doc(db, "users", uid)), 10000, "recheck profile");
      diag("profile recheck", { attempt, found: snap.exists() });
      if (snap.exists()) return false;
    } catch (error) {
      diag("profile recheck failed", { attempt, code: (error as { code?: string })?.code });
      return false;
    }
  }
  return true;
}

/** Staff, or a user whose saved subscription is still active (or paused). */
function canStartFromSnapshot(user: User): boolean {
  return hasActiveSubscription(user);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [unreadCountsMap, setUnreadCountsMap] = useState<Record<string, number>>({});
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [tourStep, setTourStep] = useState<GuidedTourStep | null>(null);
  const [tourReplay, setTourReplay] = useState(false);
  const tourReplayRef = useRef(false);

  // Replaying the tour from Settings only walks through the profile steps and
  // never triggers Ursa's onboarding messages again (see ProfilePage).
  const startTourReplay = () => {
    tourReplayRef.current = true;
    setTourReplay(true);
    setTourStep("uredi_profil_button");
  };

  const endTourReplay = () => {
    tourReplayRef.current = false;
    setTourReplay(false);
    setTourStep(null);
  };

  const env = import.meta.env.VITE_ENV;
  const isMobile = env === "mobile";

  // useNavigate() re-renders its component on every URL change (even a tab's
  // #hash), and a re-render here re-rendered every screen using useAuth(). So
  // it's used in a small child instead, which hands it over through this ref.
  const navigateRef = useRef<NavigateFunction | null>(null);

  const handleUser = (appUser: User | null) => {
    setUser(appUser);
  };

  // Setup push notifications when user is logged in
  useEffect(() => {
    if (user?.id && isMobile && !fcmToken) {
      setupPush({
        onToken: (val) => setFcmToken(val),
        onActionPerformed: (notification) => {
          const data = notification.data;
          if (data?.type === "chat_message" && data?.groupId) {
            navigateRef.current?.(`/chat?groupId=${data.groupId}`);
          }
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isMobile]);

  // Register push token whenever user LOGIN matches with a TOKEN
  useEffect(() => {
    if (user?.id && fcmToken) {
      console.log(`[Push] Registering token for user ${user.id}`);
      const registerToken = httpsCallable(functions, "registerPushToken");
      registerToken({ token: fcmToken })
        .then((result) => {
          console.log("[Push] Token registered successfully:", result);
        })
        .catch((error) => {
          console.warn("[Push] Failed to register token:", error);
        });
    }
  }, [user?.id, fcmToken]);

  const refreshUser = async () => {
    if (auth.currentUser?.uid) {
      clearUsersCache(); // Invalidate cache before fetching
      const appUser = await findUserById(auth.currentUser.uid);
      // A read that wrongly says "not found" must not sign anyone out here.
      if (!appUser) {
        diag("refresh: own profile not found, keeping the current one");
        return;
      }
      handleUser(appUser);
      // Don't reset the tour in the middle of a replay.
      if (tourReplayRef.current) return;
      if (appUser?.profileSetupCompleted === false) {
        setTourStep("start");
      } else {
        setTourStep(null);
      }
    }
  };

  // Removes every push token this device has registered from the signed-in
  // user, so the next account on this phone doesn't keep receiving their
  // notifications. (iOS used to register two different tokens per device and
  // only the one in React state was removed.)
  const removeDevicePushTokens = async () => {
    if (!auth.currentUser) return;
    const tokens = await getDevicePushTokens(fcmToken);
    if (!tokens.length) return;
    try {
      await withTimeout(
        updateDoc(doc(db, "userPushTokens", auth.currentUser.uid), {
          tokens: arrayRemove(...tokens),
        }),
        6000,
        "remove push tokens",
      );
    } catch (error) {
      console.warn("[Push] Could not remove push tokens on logout", error);
    }
  };

  // Leaves a line in the diagnostic log whenever a login ends, so a sign-out
  // nobody asked for can be traced (logout() and the checks above log theirs).
  //
  // A login that ends while the app is open also ends it in the app. Firebase
  // only does that when the account itself is gone (deleted, disabled, or
  // its password changed elsewhere), never for a bad connection. Before, the
  // app kept showing the person as logged in from the copy saved on the
  // device, and every request was refused (an empty food list, "Missing or
  // insufficient permissions").
  useEffect(() => {
    let signedInAs: string | null = null;
    return onAuthStateChanged(auth, (firebaseUser) => {
      if (signedInAs && !firebaseUser) {
        diag("login ended", { online: navigator.onLine });
        clearLocalSnapshots();
        clearUsersCache();
        handleUser(null);
      }
      signedInAs = firebaseUser?.uid ?? null;
    });
  }, []);

  useEffect(() => {
    console.log('🔐 Starting auth initialization...');

    let cancelled = false;

    const checkAuthState = async () => {
      try {
        // auth.authStateReady() resolves once Firebase has finished restoring
        // the persisted session from storage — no blind timeouts needed.
        // Start right away from the profile saved on this device when Firebase
        // has a saved session: auth.authStateReady() below waits for a server
        // check of the account on every start. Data requests wait for Auth by
        // themselves, and a session that turns out invalid is handled below.
        const hintedUid = loadSignedInUidHint();
        const early = hintedUid ? loadProfileSnapshot(hintedUid) : null;
        const startedEarly = !!early && canStartFromSnapshot(early);
        if (early && startedEarly) {
          diag("instant start (before login check)");
          handleUser(early);
          setTourStep(early.profileSetupCompleted === false ? "start" : null);
          setLoading(false);
        }

        const authStarted = performance.now();
        await auth.authStateReady();

        if (cancelled) return;

        const currentUser = auth.currentUser;
        diag("auth ready", { signedIn: !!currentUser, ms: Math.round(performance.now() - authStarted) });
        if (startedEarly && currentUser?.uid !== early?.id) {
          // The saved session was no longer valid (or belongs to someone else).
          diag("saved session no longer valid");
          clearLocalSnapshots();
          handleUser(null);
          setTourStep(null);
        }
        console.log('🔐 Auth ready. Current user:', currentUser ? `User: ${currentUser.uid}` : 'No user');

        // Instant start: show the profile saved on this device right away and
        // refresh it from the server in the background. Only when that copy
        // can't send the user somewhere wrong (an expired subscription would
        // redirect to the payment page before the fresh copy arrives).
        const saved = currentUser ? loadProfileSnapshot(currentUser.uid) : null;
        if (currentUser && saved && canStartFromSnapshot(saved)) {
          diag("own profile from device copy (instant start)");
          handleUser(saved);
          setTourStep(saved.profileSetupCompleted === false ? "start" : null);
          setLoading(false);
          const started = performance.now();
          findUserById(currentUser.uid)
            .then(async (fresh) => {
              if (cancelled) return;
              diag("own profile refreshed", { ms: Math.round(performance.now() - started), found: !!fresh });
              if (!fresh) {
                // Only an account that was really deleted ends the session.
                // Someone already signed in: much more patience (~35 s).
                if (!(await accountIsGone(currentUser.uid, [5000, 10000, 20000])) || cancelled) return;
                diag("signing out: account deleted");
                clearLocalSnapshots();
                await signOut(auth).catch(() => undefined);
                handleUser(null);
                return;
              }
              setUser((prev) => replaceIfChanged(prev, fresh));
              if (!tourReplayRef.current) {
                setTourStep(fresh.profileSetupCompleted === false ? "start" : null);
              }
            })
            .catch((error) => console.warn("Background profile refresh failed", error));
          return;
        }

        if (currentUser && currentUser.uid) {
          console.log('🔐 Fetching user data from Firestore...');
          // Keep trying while the connection is bad (the loading screen offers
          // a reload meanwhile): giving up here showed the login screen to a
          // user who is signed in.
          let appUser: User | null = null;
          for (let attempt = 1; ; attempt++) {
            try {
              appUser = (await findUserByIdResilient(currentUser.uid, (fresh) => {
                // The saved copy was shown; replace it with the server's.
                if (!cancelled && fresh) setUser((prev) => replaceIfChanged(prev, fresh));
              })) ?? null;
              break;
            } catch (error) {
              if (cancelled || auth.currentUser?.uid !== currentUser.uid) throw error;
              diag("own profile load failed, retrying", {
                attempt,
                code: (error as { code?: string })?.code,
              });
              await new Promise((resolve) => setTimeout(resolve, Math.min(2000 * attempt, 10000)));
              if (cancelled) return;
            }
          }
          if (cancelled) return;
          console.log('✅ User data fetched:', appUser ? 'Success' : 'Not found');
          if (!appUser && !(await accountIsGone(currentUser.uid))) {
            // The profile exists after all (the first read was wrong).
            appUser = (await findUserById(currentUser.uid).catch(() => undefined)) ?? null;
          }
          if (cancelled) return;
          if (!appUser) {
            // Signed in to Firebase Auth but no account in the app: sign out so
            // the login screen and Firebase agree (otherwise login "does nothing").
            diag("signing out: no profile for this login");
            await signOut(auth).catch(() => undefined);
          }
          handleUser(appUser || null);
          if (appUser?.profileSetupCompleted === false) {
            setTourStep("start");
          } else {
            setTourStep(null);
          }
        } else {
          console.log('🔐 No authenticated user');
          handleUser(null);
          setTourStep(null);
        }

        console.log('✅ Setting loading to false');
        setLoading(false);
      } catch (error) {
        if (cancelled) return;
        console.error('❌ Error checking auth state:', error);
        setLoading(false);
      }
    };

    checkAuthState();

    return () => {
      console.log('🔐 Cleaning up auth check');
      cancelled = true;
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  const login = async (email: string, pass: string): Promise<LoginResult> => {
    // Note: the global `loading` flag is intentionally not used here. It
    // replaced the whole app with a spinner, and if any write below hung
    // (offline), the app stayed on "Loading..." until restarted.
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), pass);
      const firebaseUser = userCredential.user;
      console.log('✅ Login successful, user:', firebaseUser.uid);

      let appUser = await findUserByIdResilient(firebaseUser.uid);
      if (!appUser && !(await accountIsGone(firebaseUser.uid))) {
        appUser = await findUserById(firebaseUser.uid).catch(() => undefined);
      }
      if (!appUser) {
        await signOut(auth).catch(() => undefined);
        return { ok: false, reason: "no-account" };
      }

      const nowIso = new Date().toISOString();
      // Keeps the session manager from logging the user out right away.
      // Not awaited: Firestore writes only resolve once the server confirms.
      updateUserProfile(firebaseUser.uid, {
        lastActiveAt: nowIso,
        lastLogin: nowIso,
      }).catch((e) => console.error("Failed to update session on login", e));

      const updatedAppUser = { ...appUser, lastActiveAt: nowIso, lastLogin: nowIso };

      // Trigger initial onboarding only on first login
      if (!appUser.lastLogin && !appUser.profileSetupCompleted) {
        try {
          await withTimeout(
            triggerOnboardingFlowAction(firebaseUser.uid, "initial"),
            20000,
            "onboarding",
          );
          const updated = await withTimeout(findUserById(firebaseUser.uid), 8000, "reload user");
          handleUser(updated ? { ...updated, lastLogin: nowIso, lastActiveAt: nowIso } : updatedAppUser);
        } catch (e) {
          console.warn("Onboarding did not finish during login", e);
          handleUser(updatedAppUser);
        }
        setTourStep("chat_icon");
      } else if (updatedAppUser.profileSetupCompleted === false) {
        handleUser(updatedAppUser);
        setTourStep("start");
      } else {
        handleUser(updatedAppUser);
        setTourStep(null);
      }
      return { ok: true };
    } catch (error) {
      const code = (error as { code?: string })?.code;
      if (
        code === "auth/invalid-credential" ||
        code === "auth/wrong-password" ||
        code === "auth/user-not-found" ||
        code === "auth/invalid-email" ||
        code === "auth/invalid-login-credentials"
      ) {
        return { ok: false, reason: "invalid-credentials" };
      }
      console.error("Firebase login error", error);
      if (code === "auth/network-request-failed" || (error as Error)?.name === "TimeoutError") {
        return { ok: false, reason: "network" };
      }
      return { ok: false, reason: "unknown" };
    }
  };

  const logout = async () => {
    diag("logout (by the user)");
    try {
      await removeDevicePushTokens();
      await signOut(auth);
    } catch (error) {
      console.error("Nije se moglo odjaviti", error);
    } finally {
      setFcmToken(null);
      handleUser(null);
      clearUsersCache();
      clearLocalSnapshots();
      window.location.href = "/";
    }
  };

  const toggleMuteGroup = async (groupId: string) => {
    if (!user) return;
    const mutedGroups = user.mutedGroups || [];
    const isMuted = mutedGroups.includes(groupId);
    const newMutedGroups = isMuted
      ? mutedGroups.filter(id => id !== groupId)
      : [...mutedGroups, groupId];

    try {
      await updateUserProfile(user.id, { mutedGroups: newMutedGroups });
      setUser({ ...user, mutedGroups: newMutedGroups });
    } catch (error) {
      console.error("Failed to toggle mute:", error);
    }
  };

  // Keep a copy of the profile on the device so the next start can show the
  // app right away even if the server is slow to answer.
  useEffect(() => {
    // Only for the account signed in here (another tab may have signed out).
    if (user && auth.currentUser?.uid === user.id) saveProfileSnapshot(user);
  }, [user]);

  // A trainer can pause, resume or extend a subscription while the person is
  // in the app: keep those fields live so access changes right away. Only
  // these fields are taken over; a missing or failed read never signs anyone
  // out (see accountIsGone for that).
  // The same goes for the inactivity block (lib/inactivity.ts): a trainer's
  // unblock and the person's own ticks apply right away.
  useEffect(() => {
    if (!user?.id || user.role !== "user") return;
    const uid = user.id;
    // First start of a version that tracks inactivity: the 3 days count from
    // now, not from before the feature existed. Read from the server, so an
    // old copy on the device can't restart someone's count.
    getDocFromServer(doc(db, "users", uid))
      .then((snap) => {
        if (snap.exists() && !snap.data().trainingCheckedAt) return recordTrainingChecked(uid);
      })
      .catch(() => {
        // Offline: try again on the next start.
      });
    return onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() as Partial<User>;
        if (snap.metadata.hasPendingWrites) {
          // This device's own tick: only the tick time is taken over.
          setUser((prev) =>
            prev && prev.id === uid && data.trainingCheckedAt && prev.trainingCheckedAt !== data.trainingCheckedAt
              ? { ...prev, trainingCheckedAt: data.trainingCheckedAt }
              : prev,
          );
          return;
        }
        setUser((prev) => {
          if (!prev || prev.id !== uid) return prev;
          const same =
            prev.subscriptionEndDate === data.subscriptionEndDate &&
            JSON.stringify(prev.subscriptionPause ?? null) === JSON.stringify(data.subscriptionPause ?? null) &&
            prev.trainingCheckedAt === data.trainingCheckedAt &&
            JSON.stringify(prev.inactivityReset ?? null) === JSON.stringify(data.inactivityReset ?? null);
          if (same) return prev;
          diag("subscription changed", { paused: !!data.subscriptionPause });
          return {
            ...prev,
            subscriptionEndDate: data.subscriptionEndDate,
            subscriptionPause: data.subscriptionPause,
            trainingCheckedAt: data.trainingCheckedAt,
            inactivityReset: data.inactivityReset,
          };
        });
      },
      (error) => diag("subscription listener failed", { code: (error as { code?: string })?.code }),
    );
  }, [user?.id, user?.role]);

  // "Odjavi se sa svih drugih uređaja" on another device (lib/sessions.ts):
  // a login older than that ends here right away, for every role (it's how a
  // trainer who knows a shared account's password is shut out).
  useEffect(() => {
    if (!user?.id) return;
    const uid = user.id;
    let ended = false;
    return onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        const revokedAt = snap.data()?.sessionsRevokedAt;
        if (ended || typeof revokedAt !== "string") return;
        void loginWasEnded(revokedAt).then((wasEnded) => {
          if (!wasEnded || ended) return;
          ended = true;
          diag("logged out from another device");
          void logout();
        });
      },
      () => {
        // A failed read never logs anyone out.
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // SESSION MANAGEMENT: Track user activity
  useEffect(() => {
    if (user && user.id) {
      const manageSession = async () => {
        const now = new Date();
        const lastActive = user.lastActiveAt ? new Date(user.lastActiveAt) : now;
        const diffInMs = now.getTime() - lastActive.getTime();

        // Only update if it's been a while (e.g. 1 hour) to reduce database writes
        const oneHourInMs = 60 * 60 * 1000;
        if (diffInMs > oneHourInMs) {
          try {
            await updateUserProfile(user.id, { lastActiveAt: now.toISOString() });
          } catch (e) {
            console.warn("Failed to update session activity:", e);
          }
        }
      };

      manageSession();
    }
  }, [user?.id]); // Run when user ID changes (initial load/login)

  const value: AuthContextType = {
    user,
    setUser,
    loading,
    login,
    logout,
    refreshUser,
    unreadChatCount,
    setUnreadChatCount,
    unreadCountsMap,
    setUnreadCountsMap,
    fcmToken,
    setFcmToken,
    tourStep,
    setTourStep,
    toggleMuteGroup,
    tourReplay,
    startTourReplay,
    endTourReplay,
  };

  return (
    <AuthContext.Provider value={value}>
      <NavigateRef target={navigateRef} />
      {children}
    </AuthContext.Provider>
  );
}

function NavigateRef({ target }: { target: React.MutableRefObject<NavigateFunction | null> }) {
  target.current = useNavigate();
  return null;
}
